/*eslint complexity: ["error", 37]*/
import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import {
  GetSalesInvoiceDto,
  GetSalesInvoiceDtoStatusEnum,
  GetSalesInvoiceDtoStatusKsefEnum,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import momencik from '../../../common/momencik';
import { SalesInvoiceStatusEnumText } from '../../../common/spdCore/validation/enums';
import CopyButton from '../../../common/components/CopyButton';

const renderPrintIcon = (
  salesInvoice?: GetSalesInvoiceDto,
  onPrint?: () => void,
  onPreview?: () => void,
) => {
  const isApproved =
    salesInvoice?.status !== GetSalesInvoiceDtoStatusEnum.Draft;
  const ksefAccepted =
    salesInvoice?.status === GetSalesInvoiceDtoStatusEnum.InKSeF;
  const canPrint = isApproved && (!salesInvoice?.toKsef || ksefAccepted);

  if (salesInvoice?.id && salesInvoice.active && canPrint && onPrint) {
    return (
      <OverlayTrigger overlay={<Tooltip>Otwórz wydruk faktury</Tooltip>}>
        <Icon.FilePdf
          className="icon-blue invoice-print-icon"
          size={20}
          onClick={onPrint}
        />
      </OverlayTrigger>
    );
  } else if (salesInvoice?.id && salesInvoice.active && onPreview) {
    return (
      <OverlayTrigger overlay={<Tooltip>Podgląd faktury</Tooltip>}>
        <Icon.FilePdf
          className="icon-red invoice-print-icon"
          size={20}
          onClick={onPreview}
        />
      </OverlayTrigger>
    );
  }
  return null;
};

export const buildSalesInvoiceTubeTitle = (
  salesInvoice?: GetSalesInvoiceDto,
  onPrint?: () => void,
  onPreview?: () => void,
  onNavigateToOriginal?: (id: string) => void,
) => {
  if (!salesInvoice) {
    return 'Nowa faktura sprzedaży';
  }

  const isCorrection = !!salesInvoice.originalInvoice?.id;
  const invoiceLabel = isCorrection ? 'Faktura korygująca' : 'Faktura';
  const originalInvoice = salesInvoice.originalInvoice;
  const originalNumberText = originalInvoice?.number
    ? `do faktury ${originalInvoice.number}${
        originalInvoice.systemNumber
          ? ` (nr sys. ${originalInvoice.systemNumber})`
          : ''
      }`
    : '';
  const originalNumberJsx =
    originalNumberText && originalInvoice?.id ? (
      <>
        {' '}
        {originalNumberText}
        {onNavigateToOriginal && (
          <>
            {' '}
            <Icon.BoxArrowUpRight
              className="ms-1 mb-1 icon-clickable icon-primary"
              title="Otwórz fakturę korygowaną w nowym oknie"
              onClick={() => onNavigateToOriginal(originalInvoice.id)}
            />
          </>
        )}
      </>
    ) : originalNumberText ? (
      <> {originalNumberText}</>
    ) : null;

  const ksefStatusInfo =
    salesInvoice.toKsef && salesInvoice.statusKsef
      ? ({
          [GetSalesInvoiceDtoStatusKsefEnum.WaitingForKSeF]: {
            label: 'KSEF: oczekuje',
            className: 'text-warning fw-bold ms-2',
          },
          [GetSalesInvoiceDtoStatusKsefEnum.RejectedInKSeF]: {
            label: 'KSEF: odrzucono',
            className: 'text-danger fw-bold ms-2',
          },
          [GetSalesInvoiceDtoStatusKsefEnum.AcceptedInKSeF]: null,
        }[salesInvoice.statusKsef] ?? null)
      : null;

  return (
    <>
      <div>
        {invoiceLabel} {salesInvoice.number || salesInvoice.systemNumber || ''}
        {salesInvoice.number && (
          <CopyButton
            value={salesInvoice.number}
            tooltipText="Kopiuj numer FVS"
          />
        )}
        {salesInvoice.number && salesInvoice.systemNumber
          ? ` (nr sys. ${salesInvoice.systemNumber})`
          : ''}
        {originalNumberJsx}
        {salesInvoice.status && (
          <span className="fw-bold ms-2">
            [
            {(
              SalesInvoiceStatusEnumText[salesInvoice.status] ??
              salesInvoice.status
            ).toUpperCase()}
            ]
          </span>
        )}
        {ksefStatusInfo && (
          <span className={ksefStatusInfo.className}>
            [{ksefStatusInfo.label}]
          </span>
        )}
        {salesInvoice.toKsef &&
          salesInvoice.statusKsef ===
            GetSalesInvoiceDtoStatusKsefEnum.RejectedInKSeF &&
          salesInvoice.ksefRejectionReason && (
            <OverlayTrigger
              overlay={
                <Tooltip id="ksef-rejection-tooltip">
                  {salesInvoice.ksefRejectionReason}
                </Tooltip>
              }
            >
              <Icon.ExclamationTriangleFill
                className="text-danger ms-2"
                size={16}
              />
            </OverlayTrigger>
          )}
        {!salesInvoice.active && (
          <span className="text-danger fw-bold ms-2">ANULOWANA</span>
        )}
        {renderPrintIcon(salesInvoice, onPrint, onPreview)}
        {salesInvoice.company && ` - ${salesInvoice.company.name}`}
        {salesInvoice.examined &&
          ` - ${salesInvoice.examined.surname} ${salesInvoice.examined.firstname}`}
      </div>
      {salesInvoice.examNumbers && salesInvoice.examNumbers.length > 0 && (
        <div className="invoice-corrections-subtitle">
          {`Zafakturowane badania: ${salesInvoice.examNumbers.join(', ')}`}
        </div>
      )}
      {salesInvoice.corrections && salesInvoice.corrections.length > 0 && (
        <div className="invoice-corrections-subtitle">
          {`Jest korygowana przez: ${salesInvoice.corrections.map(c => c.number || `nr sys. ${c.systemNumber}`).join(', ')}`}
        </div>
      )}
      {salesInvoice.emailSentDate && salesInvoice.emailSentTo && (
        <div className="invoice-email-sent-info">
          {`Wysłana e-mailem do: ${salesInvoice.emailSentTo} dn. ${momencik(salesInvoice.emailSentDate, 'L LT')}`}
        </div>
      )}
      {salesInvoice.autoGenerated && (
        <div className="invoice-email-sent-info fst-italic">
          {salesInvoice.autoApproved
            ? 'Wygenerowana i zatwierdzona automatycznie.'
            : 'Wygenerowana automatycznie.'}
        </div>
      )}
    </>
  );
};
